import { useEffect, useRef, useState } from 'react';

import { useCachedFeatureMenuLazyQuery } from 'features/content/hooks/use-feature-menu-query';
import { useStoreContext } from 'features/store';

import { useMenuVariant } from 'experiments/menu-variant/use-menu-variant';
import { IFeatureMenuQuery } from 'generated/sanity-graphql';
import { useLocale } from 'state/intl';
import { LANGUAGES } from 'state/intl/types';

interface IUseFeatureMenuValue {
  menuId: string;
  isMenuExperimentVariantSelected: boolean;
  menuExperimentExposureFlagKey: string | undefined;
  menuExperimentExposurePathname: string | undefined;
  menuVariantId: string | undefined;
  featureMenu: IFeatureMenuQuery['FeatureMenu'];
  featureMenuLoading: boolean;
  variantName: string | undefined;
}

interface IUseFeatureMenuOptions {
  isEnabled?: boolean;
}

const FEATURE_MENU_SINGLETON_ID = 'feature-menu-singleton';

/** Fetches shared Feature Menu configuration and resolves variants only for Legacy Menu stores. */
export const useFeatureMenu = ({
  isEnabled = true,
}: IUseFeatureMenuOptions = {}): IUseFeatureMenuValue => {
  const { language } = useLocale();
  const { noStoreSelected, store } = useStoreContext();
  const shouldFetchFeatureMenu = isEnabled && Boolean(store?.number) && !noStoreSelected;
  const isLegacyMenuVariantEnabled = shouldFetchFeatureMenu && !store?.isOneMenu;
  // Which language the current fetch cycle settled for, and whether it settled with usable
  // data. Apollo's own `data`/`called` don't reset when `variables` change, so on the render
  // right after a language switch they can still reflect the previous language - this ref is
  // the source of truth for "data actually matches the current language". `hasData: false`
  // (set in onError) keeps an errored cycle from having its stale/absent `data` read as current.
  const settledRef = useRef<{ language: LANGUAGES; hasData: boolean } | null>(null);
  const [, forceRender] = useState(0);

  // withLazySimpleCache's cache key is a plain JSON.stringify of `variables` - including
  // `language` here (Apollo ignores it, it's not in the query document) gives each language its
  // own cache entry, so a fresh mount mid-switch can never read a different language's cache.
  const [fetchFeatureMenu, { data, loading, called }] = useCachedFeatureMenuLazyQuery({
    variables: { featureMenuId: FEATURE_MENU_SINGLETON_ID, language } as any,
    onCompleted: () => {
      settledRef.current = { language, hasData: true };
      forceRender(tick => tick + 1);
    },
    onError: () => {
      settledRef.current = { language, hasData: false };
      forceRender(tick => tick + 1);
    },
  } as any);

  useEffect(() => {
    if (!shouldFetchFeatureMenu) {
      return;
    }

    // Deliberately not passing `variables` here: withLazySimpleCache's cache key is
    // `{ ...baseOptions.variables, ...fetchTimeVariablesRef.current }`, and fetch-time variables
    // win on overlapping keys. Passing them would pin the cache key to whatever language was
    // current the last time this effect ran, until it re-runs for the new one.
    fetchFeatureMenu({ allowFromCache: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, shouldFetchFeatureMenu]);

  const isSettledForCurrentLanguage = settledRef.current?.language === language;
  const featureMenu =
    shouldFetchFeatureMenu && isSettledForCurrentLanguage && settledRef.current?.hasData
      ? data?.FeatureMenu ?? null
      : null;
  const menuVariant = useMenuVariant(featureMenu, { isEnabled: isLegacyMenuVariantEnabled });
  const isMenuExperimentPending = menuVariant?.isPending === true;
  const menuId = isMenuExperimentPending
    ? ''
    : menuVariant?.menuVariantId || featureMenu?.defaultMenu?._id || '';

  const featureMenuLoading =
    shouldFetchFeatureMenu &&
    (!isSettledForCurrentLanguage || !called || loading || isMenuExperimentPending);

  return {
    isMenuExperimentVariantSelected: menuVariant?.isVariantSelected === true,
    menuId,
    menuExperimentExposureFlagKey: menuVariant?.exposureFlagKey,
    menuExperimentExposurePathname: menuVariant?.exposurePathname,
    menuVariantId: menuVariant?.menuVariantId,
    featureMenu,
    featureMenuLoading,
    variantName: menuVariant?.menuVariantId ? menuVariant.variantName : undefined,
  };
};
